import { useState } from 'react';

import { ApiError } from '../../lib/api.js';
import { PasswordInput } from '../../components/PasswordInput.js';
import '../checkout/begin.css';

/**
 * Reset a forgotten password.
 *
 * Two steps in one screen. First an email — a code is sent to it, and the answer is the same whether
 * or not the address is registered, so a stranger cannot learn which emails have accounts. Then the
 * code and a new password. The code is Cognito's own, delivered by its low-volume email, so this
 * works without any mail infrastructure of ours.
 */
const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

async function post(path: string, body: unknown): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text();
    const error = (text ? JSON.parse(text) : {}) as { error?: string; message?: string };
    throw new ApiError(response.status, error.error ?? 'INTERNAL', error.message ?? 'Something went wrong.');
  }
}

export function Forgot() {
  const [stage, setStage] = useState<'request' | 'reset' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/v1/account/forgot-password', { email: email.trim() });
      setStage('reset');
    } catch {
      // The request never reveals whether the address exists, so move on regardless.
      setStage('reset');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/v1/account/reset-password', {
        email: email.trim(),
        code: code.trim(),
        password,
      });
      setStage('done');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'done') {
    return (
      <div className="begin__centered">
        <h1 className="complete__title">Password changed.</h1>
        <p className="complete__body">
          You can now sign in with your new password.
        </p>
        <a className="button button--primary complete__action" href="/login">
          Sign in
        </a>
      </div>
    );
  }

  return (
    <div className="begin__centered">
      <p className="begin__eyebrow">Reset your password</p>
      <h1 className="complete__title">{stage === 'request' ? 'Forgot your password?' : 'Enter your code'}</h1>

      {stage === 'request' ? (
        <form
          className="login"
          onSubmit={(event) => {
            event.preventDefault();
            void requestCode();
          }}
        >
          <p className="complete__body">
            Enter your email and we will send a code to reset your password.
          </p>
          <label className="begin__field">
            <span className="begin__label">Email</span>
            <input
              className="begin__input"
              type="email"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <button
            type="submit"
            className="button button--primary begin__submit"
            disabled={busy || email.trim() === ''}
          >
            {busy ? 'Sending…' : 'Send the code'}
          </button>
        </form>
      ) : (
        <form
          className="login"
          onSubmit={(event) => {
            event.preventDefault();
            void reset();
          }}
        >
          <p className="complete__body">
            We sent a code to {email}. Enter it below with your new password.
          </p>
          <label className="begin__field">
            <span className="begin__label">Code from your email</span>
            <input
              className="begin__input"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          <label className="begin__field">
            <span className="begin__label">New password</span>
            <PasswordInput value={password} autoComplete="new-password" onChange={setPassword} />
          </label>
          {error ? <p className="begin__error">{error}</p> : null}
          <button
            type="submit"
            className="button button--primary begin__submit"
            disabled={busy || code.trim() === '' || password.length < 12}
          >
            {busy ? 'Changing…' : 'Change my password'}
          </button>
        </form>
      )}
    </div>
  );
}
